import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, ForwardStepper, OrderTimeline } from "@/components/OrderBits";
import { statusLabel, isReturn, inr } from "@/lib/orderFlow";
import { printInvoice, printLabel } from "@/lib/orderDocs";
import { downloadCSV } from "@/lib/csv";

const BUCKETS = [
  { k: "to_verify", label: "To verify", match: (s, o) => s === "ordered" },
  { k: "attention", label: "Needs attention", match: (s) => s === "fulfilment_hold" },
  { k: "released", label: "Released", match: (s) => s === "admin_verified" },
  { k: "fulfilment", label: "In fulfilment", match: (s) => ["packing", "ready_to_dispatch"].includes(s) },
  { k: "transit", label: "In transit", match: (s) => ["dispatched", "in_transit", "out_for_delivery"].includes(s) },
  { k: "delivered", label: "Delivered", match: (s) => s === "delivered" },
  { k: "returns", label: "Returns", match: (s) => isReturn(s) },
  { k: "rto", label: "RTO", match: (s) => ["rto_in_transit", "rto_delivered"].includes(s) },
  { k: "closed", label: "Closed", match: (s) => ["completed", "cancelled", "refunded", "credit_issued"].includes(s) },
  { k: "all", label: "All", match: () => true },
];

const MANUAL_STATES = [
  "admin_verified", "packing", "ready_to_dispatch", "dispatched",
  "in_transit", "out_for_delivery", "delivered", "completed",
];

function CancelDialog({ order, open, onClose, onSaved, rto }) {
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("refund");
  const submit = async () => {
    try {
      const path = rto ? "rto-refund" : "cancel";
      await api.post(`/admin/orders/${order.id}/${path}`, { reason, refund_method: method });
      toast.success(rto ? "RTO settled" : "Order cancelled & refund queued");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{rto ? "Settle RTO" : "Cancel order"} · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">{rto ? "Refund the buyer." : "Cancel & refund."}</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          {!rto && <textarea placeholder="Reason (shown in history)…" value={reason} onChange={(e) => setReason(e.target.value)} className="underline-input min-h-[70px] w-full" />}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Refund via</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "refund", n: "Original payment" }, { v: "credit", n: "Store credit" }].map((m) => (
                <button key={m.v} onClick={() => setMethod(m.v)}
                  className={`p-3 border font-mono text-xs uppercase ${method === m.v ? "bg-foreground text-background border-foreground" : "border-foreground/40"}`}>{m.n}</button>
              ))}
            </div>
          </div>
          <p className="font-mono text-sm">Refund amount: <b>{inr(order.total)}</b></p>
          <button onClick={submit} className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Confirm</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ order, open, onClose, onSaved }) {
  const [items, setItems] = useState(null);   // per-item candidates from the API
  const [picked, setPicked] = useState({});   // old_product_id -> new_product_id
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!order) { setItems(null); setPicked({}); return; }
    api.get(`/admin/orders/${order.id}/reassign-candidates`)
      .then((r) => setItems(r.data))
      .catch(() => { toast.error("Couldn't load replacement options"); setItems([]); });
  }, [order]);
  if (!order) return null;

  const anyPicked = Object.keys(picked).length > 0;
  const submit = async () => {
    const replacements = Object.entries(picked).map(([old_product_id, new_product_id]) => ({ old_product_id, new_product_id }));
    if (!replacements.length) { toast.error("Pick at least one replacement."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/reassign`, { replacements });
      toast.success(`Reassigned to ${data.reassigned_to?.join(", ") || "new vendor"} — released to fulfil`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl border-2 border-foreground rounded-none bg-background max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reassign vendor · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">Fulfil via another vendor.</DialogTitle>
        </DialogHeader>
        <p className="mt-2 text-sm text-muted-foreground">Pick a matching product from another vendor. The buyer's price stays the same; the order goes to the new vendor to pack.</p>
        <div className="mt-4 space-y-6">
          {items === null && <p className="font-mono text-xs text-muted-foreground">Loading options…</p>}
          {items && items.length === 0 && <p className="font-mono text-xs text-muted-foreground">No items to reassign.</p>}
          {items && items.map((it) => (
            <div key={it.old_product_id} className="border border-foreground/20 p-3">
              <p className="font-serif text-lg">{it.title}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Size {it.size} · Qty {it.quantity} · currently {it.vendor_slug}</p>
              {it.candidates.length === 0 ? (
                <p className="mt-2 text-sm text-destructive">No other vendor has this in stock. Consider “Keep with vendor” or cancel & refund.</p>
              ) : (
                <div className="mt-3 grid gap-2">
                  {it.candidates.map((c) => {
                    const on = picked[it.old_product_id] === c.product_id;
                    return (
                      <button key={c.product_id} type="button"
                        onClick={() => setPicked((p) => ({ ...p, [it.old_product_id]: c.product_id }))}
                        className={`text-left border p-3 flex items-center gap-3 transition-colors ${on ? "border-foreground bg-foreground/[0.04]" : "border-foreground/25 hover:border-foreground"}`}>
                        {c.image && <img src={c.image} alt="" className="w-12 h-12 object-cover bg-secondary" />}
                        <span className="flex-1">
                          <span className="block text-sm">{c.vendor_name || c.vendor_slug}</span>
                          <span className="block font-mono text-[11px] text-muted-foreground">{inr(c.price)} · {c.stock} in stock</span>
                        </span>
                        {on && <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Selected</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <button disabled={busy || !anyPicked} onClick={submit}
            className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-40">
            {busy ? "Reassigning…" : "Reassign & release"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrackingDialog({ tracking, onClose }) {
  if (!tracking) return null;
  const { order, data } = tracking;
  return (
    <Dialog open={!!tracking} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tracking · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">{data?.current_status || statusLabel(order.status)}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <p className="font-mono text-[11px] text-muted-foreground mb-3">AWB {data?.awb || "—"}{data?.courier ? ` · ${data.courier}` : ""}</p>
          {(data?.checkpoints || []).length === 0 ? (
            <p className="text-muted-foreground text-sm">{data?.message || data?.error || "No checkpoints yet."}</p>
          ) : (
            <ol className="relative border-l-[1.5px] border-foreground/20 ml-2">
              {data.checkpoints.map((c, i) => (
                <li key={i} className="ml-4 pb-4 relative">
                  <span className="absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full bg-foreground/50 border-2 border-background" />
                  <p className="font-mono text-[11px] uppercase tracking-widest">{c.status}</p>
                  <p className="text-[11px] text-muted-foreground">{c.location} · {c.time}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("to_verify");
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rtoTarget, setRtoTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [tracking, setTracking] = useState(null);

  const load = async () => setOrders(await api.get("/admin/orders").then(r => r.data));
  useEffect(() => { load(); }, []);

  const post = async (id, path, body, msg) => {
    try { await api.post(`/admin/orders/${id}/${path}`, body || {}); toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = (id, status) => post(id, "status", { status }, `Set to ${statusLabel(status)}`);
  const openTracking = async (order) => {
    try { const { data } = await api.get(`/orders/${order.id}/tracking`); setTracking({ order, data }); }
    catch { toast.error("Failed to fetch tracking"); }
  };
  // Open the official courier label (Shiprocket PDF); falls back to the internal
  // reference label for mock shipments.
  const openLabel = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/shipping-label`);
      if (data.label_url) { window.open(data.label_url, "_blank", "noopener,noreferrer"); return; }
      if (data.mocked) { printLabel(o); return; }
      toast.message("Courier label is still generating — try again in a few seconds, or download it from your Shiprocket dashboard.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't fetch the courier label"); }
  };

  const counts = useMemo(() => BUCKETS.reduce((m, b) => ((m[b.k] = orders.filter((o) => b.match(o.status, o)).length), m), {}), [orders]);
  const activeBucket = BUCKETS.find((b) => b.k === tab);
  const shown = orders
    .filter((o) => activeBucket.match(o.status, o))
    .filter((o) => !q || o.id.toLowerCase().includes(q.toLowerCase()) || (o.buyer_name || "").toLowerCase().includes(q.toLowerCase()));

  const verifyAllHeld = async () => {
    const held = orders.filter((o) => o.status === "ordered");
    if (!held.length) return;
    for (const o of held) { try { await api.post(`/admin/orders/${o.id}/verify`); } catch { /* skip */ } }
    toast.success(`Released ${held.length} order${held.length === 1 ? "" : "s"}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Fulfilment</p>
          <h2 className="font-serif text-5xl tracking-tighter">Order flow.</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => {
            const rows = orders.map((o) => ({
              "Order ID": o.id, Buyer: o.buyer_name || "", Status: statusLabel(o.status),
              "Total (INR)": o.total || 0,
              Items: (o.items || []).map((i) => `${i.title} x${i.quantity}`).join("; "),
              City: o.address?.city || "", State: o.address?.state || "", Pincode: o.address?.pincode || "",
              AWB: o.awb || "", Courier: o.courier_name || "",
              Placed: o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN") : "",
            }));
            downloadCSV(`orders-${new Date().toISOString().slice(0, 10)}.csv`, rows) || toast.error("No orders to export");
          }} className="border border-foreground px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background whitespace-nowrap">Export CSV</button>
          <button onClick={async () => {
            try {
              const { data } = await api.get("/admin/orders/line-items");
              downloadCSV(`order-line-items-${new Date().toISOString().slice(0, 10)}.csv`, data) || toast.error("No line items to export");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
          }} className="border border-foreground px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background whitespace-nowrap" title="One row per product per order — for buying-pattern analysis">Product lines (CSV)</button>
          {tab === "fulfilment" && (
            <button onClick={async () => {
              try {
                const { data } = await api.get("/admin/manifest");
                if (data.manifest_url) window.open(data.manifest_url, "_blank", "noopener,noreferrer");
                else toast.message(data.message || "No booked shipments to manifest yet.");
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate manifest"); }
            }} className="border border-foreground px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background whitespace-nowrap" title="Courier handover sheet (Shiprocket) for in-fulfilment shipments">Manifest</button>
          )}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order / buyer…" className="underline-input max-w-[240px]" />
        </div>
      </div>

      <div className="flex gap-4 md:gap-5 border-b border-foreground mb-6 font-mono text-[11px] uppercase tracking-widest overflow-x-auto">
        {BUCKETS.map((b) => (
          <button key={b.k} onClick={() => setTab(b.k)}
            className={`py-3 border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${tab === b.k ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent hover:text-[hsl(var(--accent))]"}`}>
            {b.label}
            {counts[b.k] > 0 && <span className={`px-1.5 py-0.5 text-[9px] ${b.k === "to_verify" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-foreground text-background"}`}>{counts[b.k]}</span>}
          </button>
        ))}
      </div>

      {tab === "to_verify" && counts.to_verify > 1 && (
        <button onClick={verifyAllHeld} className="mb-4 bg-foreground text-background px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Verify &amp; release all ({counts.to_verify})</button>
      )}

      {shown.length === 0 ? (
        <div className="border border-foreground/30 p-8"><p className="font-serif text-2xl text-muted-foreground">Nothing in {activeBucket.label.toLowerCase()}.</p></div>
      ) : (
        <div className="space-y-4">
          {shown.map((o) => {
            const expanded = openId === o.id;
            const held = o.status === "ordered";
            return (
              <div key={o.id} className="border border-foreground p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Order № {o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                    <p className="font-serif text-xl mt-1">{o.buyer_name}</p>
                    <p className="text-[11px] font-mono text-muted-foreground">{o.items?.length} item(s) · {o.address?.city}, {o.address?.state} — {o.address?.pincode}</p>
                  </div>
                  <div className="text-right">
                    <StatusPill status={o.status} />
                    <p className="font-serif text-xl mt-1">{inr(o.total)}</p>
                  </div>
                </div>

                {held && o.hold_reasons?.length > 0 && (
                  <div className="mt-3 border border-[hsl(var(--accent))] p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Held for review</p>
                    <p className="text-sm mt-1">{o.hold_reasons.join(" · ")}</p>
                  </div>
                )}

                {o.status === "fulfilment_hold" && o.fulfilment_flag && (
                  <div className="mt-3 border-2 border-destructive p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Vendor can't fulfil — action needed</p>
                    <p className="text-sm mt-1">"{o.fulfilment_flag.reason || "No reason given"}"</p>
                    <p className="font-mono text-[10px] text-muted-foreground mt-1">
                      Flagged by {o.fulfilment_flag.vendor_name || o.fulfilment_flag.by_name || "vendor"}
                      {o.fulfilment_flag.at ? ` · ${new Date(o.fulfilment_flag.at).toLocaleString("en-IN")}` : ""}
                    </p>
                  </div>
                )}

                <div className="mt-4"><ForwardStepper status={o.status} /></div>

                {o.awb && <p className="mt-3 font-mono text-[11px] text-muted-foreground">AWB <span className="text-foreground">{o.awb}</span>{o.courier_name ? ` · ${o.courier_name}` : ""}{o.shipment_mocked ? " (simulated)" : ""}</p>}

                {isReturn(o.status) && o.return_reason && (
                  <div className="mt-3 border-t border-destructive/40 pt-2">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Buyer's return</p>
                    <p className="text-sm italic mt-1">"{o.return_reason}"</p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex gap-2 flex-wrap items-center">
                  {held && (
                    <>
                      <button onClick={() => post(o.id, "verify", {}, "Verified & released to vendor")}
                        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest">Verify &amp; release</button>
                      <button onClick={() => setCancelTarget(o)} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Cancel</button>
                    </>
                  )}

                  {o.status === "fulfilment_hold" && (
                    <>
                      <button onClick={() => post(o.id, "resume", {}, "Released back to vendor")}
                        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest">Keep with vendor</button>
                      <button onClick={() => setReassignTarget(o)}
                        className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Reassign vendor</button>
                      <button onClick={() => setCancelTarget(o)} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Cancel &amp; refund</button>
                    </>
                  )}

                  {["admin_verified", "packing", "ready_to_dispatch"].includes(o.status) && (
                    <>
                      <button onClick={() => printInvoice(o)} className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Invoice</button>
                      <button onClick={() => openLabel(o)} className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Label</button>
                      {!o.awb && <button onClick={() => post(o.id, "create-shipment", {}, "Shipment booked")} className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Book shipment</button>}
                      <button onClick={() => setCancelTarget(o)} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Cancel</button>
                    </>
                  )}

                  {["dispatched", "in_transit", "out_for_delivery"].includes(o.status) && (
                    <>
                      <button onClick={() => openTracking(o)} className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Track</button>
                      {o.status !== "in_transit" && <button onClick={() => setStatus(o.id, "in_transit")} className="border border-foreground px-3 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">In transit</button>}
                      {o.status !== "out_for_delivery" && <button onClick={() => setStatus(o.id, "out_for_delivery")} className="border border-foreground px-3 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Out for delivery</button>}
                      <button onClick={() => setStatus(o.id, "delivered")} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Delivered</button>
                      <button onClick={() => post(o.id, "rto", {}, "Marked RTO")} className="border border-[hsl(var(--accent))] text-[hsl(var(--accent))] px-3 py-2 text-xs font-mono uppercase tracking-widest">RTO</button>
                    </>
                  )}

                  {o.status === "delivered" && (
                    <button onClick={() => post(o.id, "complete", {}, "Order completed")} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest">Mark completed</button>
                  )}

                  {o.status === "rto_delivered" && (
                    <button onClick={() => setRtoTarget(o)} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Settle refund</button>
                  )}

                  {["return_disputed", "return_vendor_rejected"].includes(o.status) && (
                    <>
                      <button onClick={() => post(o.id, "return-final", { approve: true, notes: "" }, "Return approved")} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest">Approve return</button>
                      <button onClick={() => post(o.id, "return-final", { approve: false, notes: "" }, "Return rejected")} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Reject</button>
                    </>
                  )}
                  {["return_vendor_approved", "return_admin_approved"].includes(o.status) && (
                    <>
                      <button onClick={() => post(o.id, "complete-return", { method: "refund" }, "Refund processed")} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Refund</button>
                      <button onClick={() => post(o.id, "complete-return", { method: "credit" }, "Store credit issued")} className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Store credit</button>
                    </>
                  )}

                  {/* God-mode override + history */}
                  <select value="" onChange={(e) => e.target.value && setStatus(o.id, e.target.value)}
                    className="ml-auto bg-background border border-foreground/40 px-2 py-2 font-mono text-[10px] uppercase tracking-widest">
                    <option value="">Override…</option>
                    {MANUAL_STATES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                  <button onClick={() => setOpenId(expanded ? null : o.id)} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">{expanded ? "Hide" : "History"}</button>
                </div>

                {expanded && (
                  <div className="mt-4 border-t border-foreground/20 pt-3 grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Items</p>
                      {o.items.map((it, i) => (
                        <div key={i} className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-secondary overflow-hidden">{it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}</div>
                          <div className="flex-1"><p className="font-serif text-sm">{it.title}</p><p className="text-[11px] font-mono text-muted-foreground">Size {it.size} · Qty {it.quantity} · {it.vendor_slug}</p></div>
                          <p className="font-mono text-xs">{inr(it.price * it.quantity)}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">History</p>
                      <OrderTimeline timeline={o.timeline} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CancelDialog order={cancelTarget} open={!!cancelTarget} onClose={() => setCancelTarget(null)} onSaved={load} />
      <CancelDialog order={rtoTarget} open={!!rtoTarget} onClose={() => setRtoTarget(null)} onSaved={load} rto />
      <ReassignDialog order={reassignTarget} open={!!reassignTarget} onClose={() => setReassignTarget(null)} onSaved={load} />
      <TrackingDialog tracking={tracking} onClose={() => setTracking(null)} />
    </div>
  );
}
